import { useEffect } from "react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { api, errMsg } from "../lib/api";
import { useApp, useData } from "../lib/app";
import TaskCard from "../components/TaskCard";
import { AiLoader, Empty, Header } from "../components/bits";
import { useT } from "../lib/i18n";

const ICON = { done: "🙏", assigned: "📌", accepted: "👍", declined: "↩", join: "🙋", join_done: "🙋", waiting: "⏳", due: "📅", bill: "🥛", story: "📖", info: "💜" };
const ago = (iso) => {
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  return m < 1 ? "just now" : m < 60 ? `${m} min ago` : m < 1440 ? `${Math.round(m / 60)} h ago` : `${Math.round(m / 1440)} d ago`;
};

export default function Notifications() {
  const { me, refresh } = useApp();
  const qc = useQueryClient();
  const t = useT();
  const { data, isLoading, refetch } = useData("/notifications", { refetchInterval: 30000 });
  useEffect(() => {
    if (data) api.post("/notifications/read").then(() => qc.invalidateQueries({ queryKey: ["/summary"] })).catch(() => {});
  }, [data, qc]);
  const decide = async (mid, action) => {
    try { await api.post(`/members/${mid}/${action}`); toast.success(action === "approve" ? "Approved 💜" : "Declined"); refetch(); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (isLoading) return <AiLoader />;
  return (
    <div>
      <Header title="Notifications" kicker={me.me.name} />
      {data.waiting?.length > 0 && (
        <div className="mb-5 space-y-2" data-testid="notif-waiting">
          <p className="ul-label">{t("Waiting on others")}</p>
          {data.waiting.map((tk) => <TaskCard key={tk.id} t={tk} compact />)}
          <p className="text-xs text-[#6B6680] text-center">{t("Only the person it's given to can mark it done.")}</p>
        </div>
      )}
      {data.items.length === 0 ? <Empty emoji="🔔" text="All quiet. Nothing new for you." /> : (
        <div className="space-y-2" data-testid="notif-list">
          {data.items.map((n) => (
            <div key={n.id} className={`ul-card p-4 ${n.read ? "" : "!border-[#D9D2FF] bg-[#FBFAFF]"}`} data-testid={`notif-${n.kind}`}>
              <div className="flex gap-3">
                <span className="text-xl">{ICON[n.kind] || "💜"}</span>
                <div className="flex-1">
                  <p className="font-semibold">{n.text}</p>
                  <p className="text-xs text-[#9C97AE]">{ago(n.created_at)}{n.kind === "join_done" && n.data?.result ? ` · ${n.data.result === "approve" ? "approved" : "declined"}` : ""}</p>
                </div>
              </div>
              {n.kind === "join" && me.admin && (
                <div className="flex gap-2 mt-3">
                  <button data-testid="notif-approve-btn" className="ul-btn ul-primary flex-1" onClick={() => decide(n.data.member_id, "approve")}>{t("✅ Approve")}</button>
                  <button data-testid="notif-decline-btn" className="ul-btn ul-ghost flex-1" onClick={() => decide(n.data.member_id, "decline")}>{t("❌ Decline")}</button>
                </div>
              )}
              {n.kind === "join" && <p className="text-xs text-[#6B6680] mt-2">{t("Only people you approve can see your home.")}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
